import { useEffect, useState } from 'react';
import Spinner from './Spinner';
import { Card, CardHeader, CardBody } from './Card';

export interface LoadingCardProps {
  /** Keep the surface's own title so the section stays legible while loading. */
  title?: string;
  subtitle?: string;
  /** Meaningful loading text. Defaults to a generic line — callers should override. */
  message?: string;
  /** Minimum body height; default 160 so the card doesn't collapse. */
  height?: number;
  /** Wrap in a Card. Default true. */
  asCard?: boolean;
  /** Suppress render for the first N ms to avoid a flash on cached/fast responses. */
  delayMs?: number;
  className?: string;
}

const DEFAULT_MESSAGE = 'Loading this data…';

/**
 * The plain "or else" loader: a spinner and a line of meaningful text. Used
 * where the work has no observable phases to narrate (a single opaque request).
 */
export default function LoadingCard({
  title,
  subtitle,
  message = DEFAULT_MESSAGE,
  height = 160,
  asCard = true,
  delayMs = 200,
  className = '',
}: LoadingCardProps) {
  const [show, setShow] = useState(delayMs === 0);
  useEffect(() => {
    if (delayMs === 0) return;
    const t = setTimeout(() => setShow(true), delayMs);
    return () => clearTimeout(t);
  }, [delayMs]);

  if (!show) return null;

  const body = (
    <div
      className={`flex flex-col items-center justify-center text-center gap-3 py-8 ${className}`}
      style={{ minHeight: height }}
      aria-busy="true"
      aria-live="polite"
    >
      <Spinner size="md" />
      <p className="text-sm text-neutral-500">{message}</p>
    </div>
  );

  if (!asCard) return body;

  return (
    <Card className={className}>
      {(title || subtitle) && (
        <CardHeader>
          {title && <h3 className="text-sm font-semibold text-neutral-800">{title}</h3>}
          {subtitle && <p className="text-xs text-neutral-500 mt-0.5">{subtitle}</p>}
        </CardHeader>
      )}
      <CardBody>{body}</CardBody>
    </Card>
  );
}
